import './loanDocumentPage.scss';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import heroMeditation from '../../../assets/hero-meditation.svg';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useLoanDocumentStore from '../../../store/loanDocumentStore/useLoanDocumentStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: { userFirstName },
  } = useUserStore();

  const { handleSubmit } = useForm();

  const isSimplifiedWorkflowEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_SIMPLIFIED_LOAN_DOCUMENTS_WORKFLOW,
  );

  const {
    computed: { hasProvidedDocuments },
    isStartingWorkflow,
  } = useLoanDocumentStore();

  const formSubmit = (): Promise<void> => {
    if (isSimplifiedWorkflowEnabled) {
      navigate('/loan-documents/document-upload');
      return;
    }

    navigate('/loan-documents/list');
  };

  return (
    <div className="loan-document-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header progress={15} showDrawerToggle showLogo />

          <main>
            <figure>
              <img alt={t('imageAlts.heroMeditation')} src={heroMeditation} />
            </figure>
            <form
              data-testid={locator.pages.loanDocument.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocument.form.h1}>
                  {t('LoanDocumentPage.title', { firstName: userFirstName })}
                </h1>
                <h2 data-testid={locator.pages.loanDocument.form.h2}>
                  {t('LoanDocumentPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    color="primary"
                    data-testid={locator.pages.loanDocument.form.submitButton}
                    loading={isStartingWorkflow}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {hasProvidedDocuments
                      ? t('LoanDocumentPage.resume')
                      : t('LoanDocumentPage.continue')}
                  </Button>
                </div>
              </div>
              <footer data-testid={locator.pages.loanDocument.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentPage';
